import type { SupabaseClient } from '@supabase/supabase-js';

import type { Database } from '~/lib/database.types';

/** Bounds of accounts.pilot_days (see its check constraint). */
export const PILOT_DAYS_MIN = 1;
export const PILOT_DAYS_MAX = 365;
export const DEFAULT_PILOT_DAYS = 14;

/**
 * The prefetch response carries this header when the call started a pilot
 * clock; useDrugInteractionsPrefetch re-broadcasts it as a window event.
 */
export const PILOT_ENDS_AT_HEADER = 'x-pilot-ends-at';
export const PILOT_CLOCK_STARTED_EVENT = 'pilotClockStarted';

const DAY_MS = 24 * 60 * 60 * 1000;

interface PilotAccount {
  verified: boolean | null;
  subscription_ends_at: string | null;
  pilot_days: number | null;
}

/** "1 dzień", "14 dni" */
export function formatDays(days: number) {
  return `${days} ${days === 1 ? 'dzień' : 'dni'}`;
}

/**
 * A pilot whose clock has not started yet: the subscription is active with no
 * end date until the account's first interaction check.
 */
export function isPilotWaiting(
  account: PilotAccount | null | undefined,
): account is PilotAccount & { pilot_days: number } {
  return (
    !!account?.verified && !!account.pilot_days && !account.subscription_ends_at
  );
}

/**
 * Start the clock of a waiting pilot: subscription_ends_at = now + pilot_days.
 * The filters make a concurrent or repeated call a no-op (`started: false`).
 */
export async function startPilotClock(
  client: SupabaseClient<Database>,
  accountId: string,
  pilotDays: number,
) {
  const endsAt = new Date(Date.now() + pilotDays * DAY_MS).toISOString();

  const { data, error } = await client
    .from('accounts')
    .update({ subscription_ends_at: endsAt })
    .eq('id', accountId)
    .eq('pilot_days', pilotDays)
    .is('subscription_ends_at', null)
    .select('id');

  return { endsAt, started: !!data?.length, error };
}
